/**
 * PACKAGE 8B — Detailed Search UI.
 *
 * Single source of truth for converting between the URL's `attr.*` query
 * params and the in-memory attrFilters shape shared by SearchResults.js and
 * FilterModal.js — so neither component invents its own parsing/
 * serialization and the two can't drift from each other or from the URL.
 * Mirrors server/routes/search.js's parseAttrFilters / applyAttributeFilters
 * contract (Package 8A) exactly: number fields use `attr.<name>Min`/`Max`,
 * everything else uses `attr.<name>` (repeated for multiselect — matching
 * the browser's own `URLSearchParams.append` semantics one-to-one with
 * Express's default repeated-key array parsing).
 *
 * attrFilters shape: { [name]: {type:'number', min, max} | {type:'text', value: string | string[]} }
 * `value` is an array only for a multiselect field with more than one
 * selection — everything else is a plain string, exactly what the wire
 * format needs (the actual UI control — select vs multiselect vs text — is
 * always decided by the category schema, not by this shape).
 */

export function parseAttrParamsFromSearch(searchParams) {
  const seen = new Set();
  const result = {};

  for (const key of searchParams.keys()) {
    if (!key.startsWith('attr.') || seen.has(key)) continue;
    seen.add(key);

    const name = key.slice(5);
    const values = searchParams.getAll(key);

    if (name.endsWith('Min')) {
      const field = name.slice(0, -3);
      result[field] = result[field] || { type: 'number', min: '', max: '' };
      result[field].min = values[0];
    } else if (name.endsWith('Max')) {
      const field = name.slice(0, -3);
      result[field] = result[field] || { type: 'number', min: '', max: '' };
      result[field].max = values[0];
    } else {
      result[name] = { type: 'text', value: values.length > 1 ? values : values[0] };
    }
  }

  return result;
}

export function buildAttrURLEntries(attrFilters) {
  const entries = [];

  Object.entries(attrFilters || {}).forEach(([name, cfg]) => {
    if (!cfg) return;

    if (cfg.type === 'number') {
      if (cfg.min !== null && cfg.min !== undefined && cfg.min !== '') {
        entries.push([`attr.${name}Min`, String(cfg.min)]);
      }
      if (cfg.max !== null && cfg.max !== undefined && cfg.max !== '') {
        entries.push([`attr.${name}Max`, String(cfg.max)]);
      }
      return;
    }

    const values = Array.isArray(cfg.value) ? cfg.value : [cfg.value];
    values
      .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
      .forEach((v) => entries.push([`attr.${name}`, String(v)]));
  });

  return entries;
}

/**
 * Drops attrFilters entries that no longer belong once the field list
 * changed — a field disappeared entirely (category or a gating parent
 * value changed), or a select/multiselect value is no longer among the
 * field's current options. Returns the SAME object reference when nothing
 * needed pruning, so callers can skip a state update / effect re-run.
 */
export function pruneStaleAttrs(fields, attrFilters) {
  const fieldByName = new Map((fields || []).map((f) => [f.name, f]));
  let changed = false;
  const next = {};

  for (const [name, cfg] of Object.entries(attrFilters || {})) {
    const field = fieldByName.get(name);

    if (!field) {
      changed = true;
      continue;
    }

    if (field.type === 'number' || !Array.isArray(field.options)) {
      next[name] = cfg;
      continue;
    }

    if (Array.isArray(cfg?.value)) {
      const filtered = cfg.value.filter((v) => field.options.includes(v));
      if (filtered.length !== cfg.value.length) changed = true;
      if (filtered.length === 0) continue;
      next[name] = filtered.length === cfg.value.length ? cfg : { ...cfg, value: filtered };
    } else if (cfg?.value && !field.options.includes(cfg.value)) {
      changed = true;
    } else {
      next[name] = cfg;
    }
  }

  return changed ? next : attrFilters;
}

/**
 * Builds a plain {fieldName: value} map from attrFilters, suitable as the
 * `attrs` argument to getCategoryFieldDefs's showIf resolution — the same
 * role server/utils/attributeSchema.js's buildAttrsStateFromFilters plays
 * on the backend, kept in the same shape on purpose.
 */
export function buildAttrsStateForFieldResolution(attrFilters) {
  const state = {};

  Object.entries(attrFilters || {}).forEach(([name, cfg]) => {
    if (!cfg) return;

    if (cfg.type === 'number') {
      state[name] =
        (cfg.min !== null && cfg.min !== undefined && cfg.min !== '') ||
        (cfg.max !== null && cfg.max !== undefined && cfg.max !== '')
          ? true
          : undefined;
      return;
    }

    state[name] = Array.isArray(cfg.value) ? cfg.value[0] : cfg.value || undefined;
  });

  return state;
}
